import dayjs from "dayjs";
import { ArrowUpRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  VENUE_AUDIT_TWIN_MATCH_LABEL,
  venueAuditEventHref,
} from "@/lib/tina/venue-audit";
import type {
  VenueAuditEvidence,
  VenueAuditTwinEventPair,
  VenueAuditTwinEventSide,
} from "@/types/tina";

/**
 * A `venue_twin` finding's sample of duplicate events: the same concert published once at
 * each venue, both sides side by side so a curator can tell one place held as two records
 * from a room and its building without opening anything.
 *
 * Read defensively like every evidence field: the sample arrives as wire data, so a pair
 * missing a side, or a side missing an id, is skipped rather than allowed to blank the
 * table. Renders nothing when no pair survives.
 */
export const TwinEventPairs = ({
  evidence,
}: {
  evidence: VenueAuditEvidence;
}) => {
  const pairs = (evidence.twin_event_pairs ?? []).filter(isRenderablePair);
  if (pairs.length === 0) return null;
  // The API caps the sample at five; the count covers the whole 30-day window.
  const hidden = (evidence.twin_event_pair_count ?? 0) - pairs.length;
  return (
    <div className="mt-1 flex flex-col gap-1 border-l-2 pl-2">
      {pairs.map((pair) => (
        <div
          key={`${pair.a.mood_event_id}__${pair.b.mood_event_id}`}
          className="flex items-start gap-2"
        >
          <Badge
            variant="outline"
            className="mt-0.5 shrink-0 text-[10px] font-normal"
          >
            {VENUE_AUDIT_TWIN_MATCH_LABEL[pair.match] ?? pair.match}
          </Badge>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <TwinEventSide side={pair.a} />
            <TwinEventSide side={pair.b} />
          </div>
        </div>
      ))}
      {hidden > 0 ? (
        <span className="text-[10px] text-muted-foreground">{hidden} more</span>
      ) : null}
    </div>
  );
};

const isRenderablePair = (
  pair: VenueAuditTwinEventPair | null | undefined,
): pair is VenueAuditTwinEventPair =>
  !!pair?.a?.mood_event_id && !!pair?.b?.mood_event_id;

const TwinEventSide = ({ side }: { side: VenueAuditTwinEventSide }) => {
  // dayjs(undefined) is NOW, and valid — a missing start must never reach it.
  const start = typeof side.start === "string" ? dayjs(side.start) : null;
  return (
    <div className="flex items-baseline gap-1.5">
      <a
        href={venueAuditEventHref(side.mood_event_id)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-w-0 items-center gap-1 text-xs hover:underline"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="truncate">{side.title || side.mood_event_id}</span>
        <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
      </a>
      <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
        {start?.isValid() ? start.format("DD/MM HH:mm") : "—"}
      </span>
      {side.site ? (
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {side.site}
        </span>
      ) : null}
    </div>
  );
};
